// Импорт документов: «Открыть документ Word» (новый отчёт), «Вставить из файла» (в открытый отчёт),
// перетаскивание .docx/.md/.zip на список отчётов и в редактор, диалог «Вставить Markdown / текст».
import { useEffect, useRef, useState } from 'react';
import type { JSONContent as J } from '@tiptap/core';
import type { Ctx, DialogProps } from '../../core/registry';
import { api } from '../../core/api';
import { refreshNumbering } from '../../core/editor/numberingPlugin';
import { Btn, Modal } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { insertParsed, runPaste, type Session } from './apply';
import { showBar, setBusy } from './bar';
import { getPref } from './prefs';
import type { PasteStats } from './structure';

/** Документы Word: .docx - как есть; .doc/.rtf/.odt сервер переводит в .docx через Word (или объясняет, почему нет;
 *  защищённый паролем - как снять пароль). */
export const WORD_EXT = /\.(docx|docm|doc|rtf|odt)$/i;
export const DOC_EXT = /\.(docx|docm|doc|rtf|odt|md|markdown|txt|htm|html)$/i;
export const PROJECT_EXT = /\.(docx|docm|doc|rtf|odt|md|markdown|txt|zip)$/i;

/** Выбор файла через скрытый <input type=file> (по действию пользователя). */
export function pickFile(accept: string, multiple = false): Promise<File[]> {
  return new Promise((resolve) => {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = accept;
    inp.multiple = multiple;
    inp.style.display = 'none';
    inp.onchange = () => { resolve(Array.from(inp.files || [])); inp.remove(); };
    window.addEventListener('focus', () => setTimeout(() => { if (!inp.files?.length) { resolve([]); inp.remove(); } }, 800), { once: true });
    document.body.appendChild(inp);
    inp.click();
  });
}

function statsText(st: Partial<PasteStats> | undefined): string {
  if (!st) return '';
  const p: string[] = [];
  const add = (n: number | undefined, s: string) => { if (n) p.push(`${s}: ${n}`); };
  add((st.headings || 0) + (st.struct || 0), 'заголовков');
  add(st.figures, 'рисунков');
  add(st.tables, 'таблиц');
  add(st.formulas, 'формул');
  add(st.listings, 'листингов');
  add(st.lists, 'перечислений');
  add(st.footnotes, 'сносок');
  add(st.sources, 'источников');
  add(st.appendices, 'приложений');
  return p.join(', ');
}

/** Файл -> новый отчёт (сервер: import_project). Открывает его. */
export async function importAsProject(ctx: Ctx, file: File, open = true): Promise<string | null> {
  if (!PROJECT_EXT.test(file.name)) { ctx.toast(`«${file.name}»: открываются .docx (.doc, .rtf, .odt), .md, .txt и .zip`, 'warn', 6000); return null; }
  ctx.toast(`Импорт «${file.name}»…`, 'info', 2500);
  const r: any = await api.importProject(file);
  const st = statsText(r.report);
  const conv = r.report?.convertedFrom ? ' (документ переведён в .docx через Word)' : '';
  // замечания импорта (что с титула/бланков не перенесено, чего на титуле нет) - в журнале проверки (meta.import_notes)
  const notes = r.report?.importNotes ? ` Замечаний импорта: ${r.report.importNotes} - см. журнал «Проверки».` : '';
  // что сайт дописал сам (stoweb/autorefs.py: ссылки, абзацы-ссылки, названия) - выделено в тексте пунктиром
  const auto = r.report?.autoSummary ? ` Добавлено автоматически (выделено в тексте - проверьте): ${r.report.autoSummary}.` : '';
  // что сайт заполнил сам в «Данных работы» и источниках (meta.auto, source.auto: отмечено - проверить): тема не с
  // титула, ключевые слова, ФИО из профиля, даты обращения к электронным ресурсам
  const AUTO_NAMES: Record<string, string> = { title: 'тема', keywords: 'ключевые слова', student: 'ФИО (из профиля)',
    teacher: 'ФИО руководителя (из списка профиля)' };
  const filled = [...((r.report?.autoMeta || []) as string[]).map((k) => AUTO_NAMES[k] || k),
    ...(r.report?.autoAccessed ? [`даты обращения к источникам (${r.report.autoAccessed})`] : [])];
  const metaAuto = filled.length ? ` Заполнено сайтом: ${filled.join(', ')} - проверьте.` : '';
  ctx.toast(`Отчёт создан из «${file.name}»${conv}${st ? ' - ' + st : ''}. Проверьте данные титула («Данные работы»).${metaAuto}${auto}${notes}`,
    'ok', auto || metaAuto ? 14000 : 9000);
  if (open) location.hash = '#/p/' + encodeURIComponent(r.id);
  return r.id;
}

/** Файл -> в открытый отчёт на место курсора (или сброса). */
export async function insertDocFile(ctx: Ctx, file: File, pos: number | null): Promise<void> {
  const pid = ctx.store.getState().projectId;
  if (!pid || !ctx.editor) return;
  const low = file.name.toLowerCase();
  setBusy(true);
  try {
    if (WORD_EXT.test(low)) {
      const res = await fetch(`/api/projects/${encodeURIComponent(pid)}/paste/docx?filename=${encodeURIComponent(file.name)}`, {
        method: 'POST', headers: { 'X-STO-Token': api.token }, body: file });
      if (!res.ok) { let m = res.statusText; try { m = (await res.json()).detail || m; } catch { /* текст */ } throw new Error(m); }
      const data = await res.json();
      insertFragment(ctx, data, pos, file.name);
      return;
    }
    const text = await file.text();
    const isHtml = /\.html?$/.test(low);
    const s = await runPaste(ctx, { kind: 'command', html: isHtml ? text : '', text: isHtml ? '' : text, files: [], pos,
      markdown: /\.(md|markdown)$/.test(low) }, { mode: 'structure', links: getPref('links') });
    showBar(s);
  } finally {
    setBusy(false);
  }
}

/** Фрагмент с сервера (импорт .docx) -> в документ + источники + данные работы. */
function insertFragment(ctx: Ctx, data: { blocks: J[]; appendices: J[]; sources: any[]; meta: Record<string, any>; stats: any;
  notes?: { sev: 'E' | 'W' | 'I'; msg: string; key?: string; value?: string }[] }, pos: number | null, name: string) {
  const lib = ctx.store.getState().sources;
  const added = (data.sources || []).filter((s: any) => !lib.some((x) => x.id === s.id || (x.text && x.text === s.text)));
  if (added.length) ctx.store.getState().setSources((l) => [...l, ...added]);
  const n = insertParsed(ctx, data.blocks || [], data.appendices || [], pos);
  const meta = ctx.store.getState().meta;
  const before: Record<string, any> = {};
  if (meta && data.meta) {
    const next: any = { ...meta };
    for (const k of ['title', 'discipline', 'number', 'variant']) if (data.meta[k] && !String((meta as any)[k] || '').trim()) { before[k] = (meta as any)[k]; next[k] = data.meta[k]; }
    if (Object.keys(before).length) ctx.store.getState().setMeta(() => next);
  }
  // замечания импорта вставленного файла (что не перенесено, что дописано сайтом - docx_import) - в журнал проверки
  // открытого отчёта (meta.import_notes), с именем файла; раньше при вставке они терялись молча
  const notes = (data.notes || []).filter((x) => x && x.msg);
  if (n && notes.length && ctx.store.getState().meta) {
    ctx.store.getState().setMeta((m) => {
      const have = new Set((m.import_notes || []).map((x) => x.msg));
      const add = notes.map((x) => ({ ...x, msg: `вставка «${name}»: ${x.msg}` })).filter((x) => !have.has(x.msg));
      return add.length ? { ...m, import_notes: [...(m.import_notes || []), ...add] } : m;
    });
  }
  refreshNumbering();
  const s: Session = { input: { kind: 'command', html: '', text: '', files: [], pos }, opts: { mode: 'structure', links: 'text' },
    app: 'word', stats: { ...emptyish(), ...(data.stats || {}) }, images: null, mathErrors: 0, cache: new Map(),
    docAfter: n ? ctx.editor!.state.doc : null, addedSources: added.map((x: any) => x.id), metaBefore: Object.keys(before).length ? before : null,
    hasLinks: false, blocksCount: n, fixed: true };
  showBar(s);
  if (!n) ctx.toast(`В «${name}» не найдено текста для вставки`, 'warn', 5000);
}
function emptyish(): PasteStats {
  return { headings: 0, struct: 0, paragraphs: 0, lists: 0, figures: 0, tables: 0, formulas: 0, listings: 0, footnotes: 0,
    notes: 0, xrefs: 0, cites: 0, sources: 0, appendices: 0, missingImages: 0, emptyRemoved: 0, numbersRemoved: 0,
    recased: 0, links: 0, lostInTables: 0, titleSkipped: false, tocSkipped: false, listsFixed: 0 };
}

// ------------------------------------------------------------------------------------------------ диалоги
/** Брошен/вставлен документ Word в открытый отчёт: вставить сюда или открыть отдельным отчётом. */
export function DocChoiceDialog({ ctx, props, close }: DialogProps<'insert' | 'new'>) {
  const name: string = props.name || 'документ';
  return (
    <Modal title="Документ Word" onClose={() => close()} width={520} testid="paste-doc-dialog">
      <p className="pst-dlg-lead">Что сделать с «{name}»?</p>
      <div className="pst-choice">
        <button type="button" className="pst-choice-btn" data-testid="paste-doc-insert" onClick={() => close('insert')}>
          <Icon name="paste" size={22} /><b>Вставить в этот отчёт</b>
          <span>Текст, заголовки, рисунки, таблицы и формулы - на место курсора, по правилам СТО</span>
        </button>
        <button type="button" className="pst-choice-btn" data-testid="paste-doc-new" onClick={() => close('new')}>
          <Icon name="new" size={22} /><b>Открыть как новый отчёт</b>
          <span>Титул и задание - в «Данные работы», текст - в новый отчёт</span>
        </button>
      </div>
      <div className="pst-dlg-foot"><Btn label="Отмена" onClick={() => close()} /></div>
    </Modal>
  );
}

/** «Вставить Markdown / текст»: поле, куда можно вставить текст из чат-бота, README или PDF. */
export function MarkdownDialog({ ctx, close }: DialogProps<boolean>) {
  const [text, setText] = useState('');
  const [kind, setKind] = useState<'auto' | 'markdown' | 'text'>('auto');
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const go = async () => {
    if (!text.trim()) return;
    close(true);
    setBusy(true);
    try {
      const s = await runPaste(ctx, { kind: 'command', html: '', text, files: [], pos: null, markdown: kind === 'markdown' },
        { mode: kind === 'text' ? 'text' : 'structure', links: getPref('links') });
      showBar(s);
    } catch (e: any) { ctx.toast('Вставка: ' + (e?.message || e), 'error', 6000); } finally { setBusy(false); }
  };
  return (
    <Modal title="Вставить Markdown или текст" onClose={() => close()} width={760} testid="paste-md-dialog">
      <p className="pst-dlg-lead">Вставьте сюда ответ чат-бота, Markdown (<code>#</code> заголовки, списки, таблицы, <code>$формулы$</code>),
        диалект writer или текст из PDF - он станет заголовками, перечислениями, таблицами и формулами по СТО.</p>
      <textarea ref={ref} className="pst-md-area" value={text} onChange={(e) => setText(e.target.value)} data-testid="paste-md-text"
        placeholder={'## Раздел\n\nТекст с формулой $E = mc^2$.\n\n- пункт перечисления\n- ещё пункт'}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void go(); }} />
      <div className="pst-md-opts">
        {([['auto', 'Определить автоматически'], ['markdown', 'Markdown'], ['text', 'Простой текст']] as const).map(([k, l]) => (
          <label key={k}><input type="radio" name="pst-kind" checked={kind === k} onChange={() => setKind(k)} /> {l}</label>
        ))}
      </div>
      <div className="pst-dlg-foot">
        <Btn label="Отмена" onClick={() => close()} />
        <Btn label="Вставить" primary onClick={() => void go()} testid="paste-md-go" disabled={!text.trim()} />
      </div>
    </Modal>
  );
}
